import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { onSessionExpired } from '@/auth/sessionExpiredBus';
import { loginRequest, msalInstance } from '@/auth/msalConfig';
import './SessionExpiredBanner.css';

/**
 * Red de seguridad global (bug fila 100, UAT 2026-08-25): se monta UNA vez en `AppRouter` y escucha
 * el evento que dispara `getToken()`/`acquireTokenSilentSafe()` cuando Entra ID exige reautenticación
 * y el popup de recuperación también falla. Cubre cualquier pantalla que no maneje
 * `SessionExpiredError` explícitamente — las 4 de "Traducir auto." ya muestran su propio mensaje más
 * específico y no dependen de este banner, pero tampoco lo ocultan (no hay conflicto: mismo evento,
 * dos avisos independientes).
 *
 * No se autodescarta: a diferencia de un Toast, quedarse visible es intencional.
 *
 * Reporte QA 2026-08-27: un `window.location.reload()` NO arregla nada aquí — MSAL cachea la cuenta
 * en `localStorage` (`msalConfig.ts`), así que sobrevive al reload aunque la sesión de Entra ID ya
 * esté muerta; el siguiente `acquireTokenSilent` automático vuelve a fallar y su intento de
 * recuperación por popup (sin gesto de usuario detrás, viene de una carga de página) lo bloquea el
 * navegador, así que el banner reaparecía de inmediato. `loginRedirect` sí resuelve la sesión real
 * (navegación completa, nunca la bloquea un popup-blocker) y es el mismo mecanismo que ya usan
 * `LoginView`/`FrontlineRegisterView`. `notifySessionExpired()` solo se dispara desde la rama
 * MSAL/AAD de `client.ts`/`acquireToken.ts` (Teams y Frontline resuelven antes por su cuenta), así
 * que este banner nunca aparece en esos dos casos y no hace falta condicionar la llamada.
 */
export function SessionExpiredBanner() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => onSessionExpired(() => setVisible(true)), []);

  if (!visible) return null;

  return (
    <div className="session-expired-banner" role="alert">
      <i className="fas fa-triangle-exclamation" />
      <span>{t('common.sessionExpiredMessage')}</span>
      <button type="button" onClick={() => { void msalInstance.loginRedirect(loginRequest); }}>
        {t('common.reload')}
      </button>
    </div>
  );
}
